import { useState } from "react"
import { AreaField, PillGroup, TextField } from "../fields"

const LEVELS = ["Beginner", "Intermediate", "Advanced", "Executive"].map(l => ({ value: l, label: l }))
const DURATIONS = [
    { value: 1, label: "1 Month" },
    { value: 3, label: "3 Months" },
    { value: 6, label: "6 Months" },
    { value: 12, label: "12 Months" },
]

// Concrete, plain-language examples — real people with real problems, no corporate filler.
// Vague inputs produce vague output, so these double as the quality bar users see on first run.
// "Load example answers" rotates through them so users see the range.
const EXAMPLES = [
    {
        name: "Your First 90 Days as a Manager",
        audience: "Engineers who were just promoted to manage the team they used to be part of",
        level: "Beginner",
        challenge: "They still jump in and fix things themselves, dread giving feedback to people who were their teammates last month, and have no real way to tell if they're doing a good job.",
        outcome: "Run 1:1s people find useful, give direct feedback without dreading it, and hand off work instead of doing it all themselves.",
        durationMonths: 3,
    },
    {
        name: "Closing Your First Deals",
        audience: "New sales reps in their first six months selling software to businesses",
        level: "Beginner",
        challenge: "They get stuck after the demo, freeze on 'we'll think about it', and let deals go quiet for weeks until they die.",
        outcome: "Take a deal from first call to signed contract, handle the three objections they hear most, and keep deals moving instead of stalling.",
        durationMonths: 3,
    },
    {
        name: "Speaking Up in Meetings",
        audience: "Quiet team members who have good ideas but rarely say them out loud in a group",
        level: "Beginner",
        challenge: "They rehearse what to say, the moment passes, and someone else makes their point. Afterwards they're annoyed they stayed silent.",
        outcome: "Make their point in the moment, disagree without it getting tense, and circle back when they get talked over.",
        durationMonths: 1,
    },
    {
        name: "From Senior to Staff Engineer",
        audience: "Senior engineers who keep getting told they need 'more impact' to reach staff level",
        level: "Advanced",
        challenge: "They write great code but their work stays inside their own team, and nobody has told them what staff-level work actually looks like day to day.",
        outcome: "Pick problems that matter beyond their team, write a design doc other people get behind, and do the kind of work that gets them promoted.",
        durationMonths: 6,
    },
]

export default function BuildingBlocks({ value, onChange, onNext }) {

    const [error, setError] = useState("")
    const [exampleIdx, setExampleIdx] = useState(0)
    const set = (field, v) => onChange({ ...value, [field]: v })

    const loadExample = () => {
        onChange(EXAMPLES[exampleIdx % EXAMPLES.length])
        setExampleIdx(i => i + 1)
        setError("")
    }

    const handleNext = () => {
        const required = ["name", "audience", "level", "challenge", "outcome"]
        const missing = required.some(f => !value[f] || `${value[f]}`.trim().length === 0) || !value.durationMonths
        if (missing) { setError("Please fill in every field before continuing."); return }
        setError("")
        onNext()
    }

    return (
        <div className="card standard-padding flex columns gap-s showing">
            <div className="flex columns gap-xxs">
                <p className="card-title">Building Blocks</p>
                <p className="card-body neutral">Every track is built from a few core building blocks. Set these first; they shape everything that follows.</p>
            </div>

            <TextField label="Track name" value={value.name} onChange={v => set("name", v)} placeholder="e.g. First-Time Managers" />
            <AreaField label="Who is this track for?" hint="Q1" value={value.audience} onChange={v => set("audience", v)} placeholder="Describe the audience" minRows={2} maxRows={4} />
            <PillGroup label="What level is this track designed for?" hint="Q2" options={LEVELS} value={value.level} onChange={v => set("level", v)} />
            <AreaField label="What is their biggest challenge?" hint="Q3" value={value.challenge} onChange={v => set("challenge", v)} placeholder="Describe the core challenge" />
            <AreaField label="What outcome do they want to achieve?" hint="Q4" value={value.outcome} onChange={v => set("outcome", v)} placeholder="Describe the desired outcome" />
            <PillGroup label="How long should the track be?" hint="Q5" options={DURATIONS} value={value.durationMonths} onChange={v => set("durationMonths", v)} />

            {error && <p className="card-label error showing">{error}</p>}

            <hr className="vi-spacer-xs" />
            <div className="flex space-between center gap-xs bt-footer">
                <button className="text" onClick={loadExample}>Load example answers</button>
                <button className="solid with-icon trailing" onClick={handleNext}>
                    <span>Continue to Track DNA</span>
                    <span className="material-symbols-outlined scale-xs">chevron_right</span>
                </button>
            </div>
        </div>
    )
}
